import React, { createContext, useState, useEffect } from 'react';

export const OrgContext = createContext({
  orgId: null,
  setOrgId: () => {},
  orgLogo: null,
  setOrgLogo: () => {},
  orgName: null, // Add orgName to context
  setOrgName: () => {}, // Add setOrgName to context
  // ...other existing context values...
});

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export const OrgProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(null);

  const [orgName, setOrgName] = useState(() => {
    return localStorage.getItem('orgName') || null; // Retrieve orgName from localStorage
  });

  const [orgLogoURL, setOrgLogoURL] = useState(() => {
    return localStorage.getItem('orgLogoURL') || null;
  });

  const loadOrgId = async () => {
    try {
      const savedOrgId = localStorage.getItem('orgId');
      if (savedOrgId) {
        const parsedOrgId = parseInt(savedOrgId, 10);
        if (!isNaN(parsedOrgId)) {
          setOrgId(parsedOrgId);
          setError(null);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error loading orgId from localStorage:', error);
      setError(error.message);
      return false;
    }
  };

  useEffect(() => {
    const attemptLoad = async () => {
      const success = await loadOrgId();
      
      if (!success && retryCount < MAX_RETRIES) {
        console.log(`Retrying orgId load... Attempt ${retryCount + 1}/${MAX_RETRIES}`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, RETRY_DELAY);
      } else {
        setLoading(false);
      }
    };

    if (loading) {
      attemptLoad();
    }
  }, [retryCount, loading]);

  useEffect(() => {
    if (orgLogoURL) {
      localStorage.setItem('orgLogoURL', orgLogoURL);
    } else {
      localStorage.removeItem('orgLogoURL');
    }
  }, [orgLogoURL]);

  const handleSetOrgId = (newOrgId) => {
    try {
      if (newOrgId) {
        localStorage.setItem('orgId', newOrgId.toString());
      } else {
        localStorage.removeItem('orgId');
      }
      setOrgId(newOrgId);
      setError(null);
    } catch (error) {
      console.error('Error saving orgId to localStorage:', error);
      setError(error.message);
    }
  };

  const handleSetOrgName = (newOrgName) => {
    try {
      if (newOrgName) {
        localStorage.setItem('orgName', newOrgName);
      } else {
        localStorage.removeItem('orgName');
      }
      setOrgName(newOrgName);
    } catch (error) {
      console.error('Error saving orgName to localStorage:', error);
    }
  };

  const handleSetOrgLogoURL = (newLogoURL) => {
    setOrgLogoURL(newLogoURL);
  };

  useEffect(() => {
    console.log('OrgContext updated:', { orgId, orgLogoURL });
  }, [orgId, orgLogoURL]);

  return (
    <OrgContext.Provider 
      value={{ 
        orgId, 
        setOrgId: handleSetOrgId,
        loading,
        error,
        orgLogoURL, 
        setOrgLogoURL: handleSetOrgLogoURL,
        orgName, 
        setOrgName: handleSetOrgName
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};
